/**
 * CinematicEditorScene — Main scene shell for the visual cinematic editor.
 * Launches as overlay (pauses GameScene + UIScene), manages sub-components.
 *
 * Features:
 * - Undo/Redo (Ctrl+Z / Ctrl+Shift+Z) with snapshot history
 * - Copy/Paste/Duplicate steps (Ctrl+C / Ctrl+V / Ctrl+D)
 * - Full keyboard shortcut dispatcher (Space, Delete, arrows, etc.)
 * - Help overlay (? or F1) with shortcuts + workflow reference
 */
import Phaser from 'phaser';
import { EventBus } from '@/events/EventBus';
import { CANVAS_W, CANVAS_H, WORK_AREA, EditorEvents } from './EditorTypes';
import type { EditorState } from './EditorTypes';
import type { CinematicSequence, CinematicStep } from '../CinematicTypes';
import { TECH, fillTechRect, drawTechRect, showConfirmDialog } from '@/ui/TechUI';
import { EditorToolbar } from './EditorToolbar';
import { EditorTimeline } from './EditorTimeline';
import { EditorStepPanel } from './EditorStepPanel';
import { EditorCaptureMode } from './EditorCaptureMode';
import { EditorPreview } from './EditorPreview';
import { EditorLivePreview } from './EditorLivePreview';
import { EditorHistory } from './EditorHistory';
import { registerSequence } from '../CinematicDirector';
import { CinematicEvents } from '../CinematicEvents';

interface InitData {
  sequence?: CinematicSequence;
  callerScenes?: string[];
  hostSceneKey?: string;
}

export class CinematicEditorScene extends Phaser.Scene {
  private state!: EditorState;
  private toolbar!: EditorToolbar;
  private timeline!: EditorTimeline;
  private stepPanel!: EditorStepPanel;
  private captureMode!: EditorCaptureMode;
  private preview!: EditorPreview;
  private livePreview!: EditorLivePreview;
  private history!: EditorHistory;
  private bgGfx!: Phaser.GameObjects.Graphics;
  private originalJson = '';
  private autoSaveTimer: ReturnType<typeof setTimeout> | null = null;

  /** Clipboard for copy/paste steps */
  private clipboardStep: CinematicStep | null = null;
  /** History event handlers (stored for cleanup in shutdown) */
  private historyPushUndo = (): void => { this.history.pushUndo(); };
  private historyPushUndoDebounced = (): void => { this.history.pushUndoDebounced(); };
  private historyResetDebounce = (): void => { this.history.resetDebounce(); };
  /** Help overlay container (null when hidden) */
  private helpOverlay: Phaser.GameObjects.Container | null = null;

  constructor() {
    super({ key: 'CinematicEditorScene' });
  }

  init(data?: InitData): void {
    const defaultSeq: CinematicSequence = {
      id: 'new-sequence',
      lockCamera: true,
      lockInput: true,
      steps: [],
    };

    const hostKey = data?.hostSceneKey ?? 'GameScene';
    this.state = {
      sequence: data?.sequence ? JSON.parse(JSON.stringify(data.sequence)) : defaultSeq,
      selectedStepIndex: -1,
      playingStepIndex: -1,
      mode: 'edit',
      hostScene: this.scene.get(hostKey) ?? null,
      hostSceneKey: hostKey,
      callerScenes: data?.callerScenes ?? [hostKey],
    };
  }

  create(): void {
    // ── Track original state for dirty check ──
    this.originalJson = JSON.stringify(this.state.sequence);

    // ── Pause caller scenes + block input ──
    for (const key of this.state.callerScenes) {
      if (this.scene.isActive(key)) this.scene.pause(key);
    }
    this.lockGameInput(true);

    // ── Semi-dark background ──
    this.bgGfx = this.add.graphics().setDepth(0);
    this.drawBackground();

    // ── Undo/Redo history ──
    this.history = new EditorHistory(this.state);

    // ── Sub-components ──
    this.livePreview = new EditorLivePreview(this, this.state);
    this.toolbar = new EditorToolbar(this, this.state);
    this.toolbar.setHistory(this.history);
    this.timeline = new EditorTimeline(this, this.state);
    this.stepPanel = new EditorStepPanel(this, this.state);
    this.captureMode = new EditorCaptureMode(this, this.state);
    this.preview = new EditorPreview(this, this.state);

    // ── Event wiring ──
    EventBus.on(EditorEvents.STEP_SELECTED, this.onStepSelected, this);
    EventBus.on(EditorEvents.STEP_ADDED, this.onStepsChanged, this);
    EventBus.on(EditorEvents.STEP_REMOVED, this.onStepsChanged, this);
    EventBus.on(EditorEvents.STEPS_REORDERED, this.onStepsChanged, this);
    EventBus.on(EditorEvents.STEP_UPDATED, this.onStepUpdated, this);
    EventBus.on(EditorEvents.SEQUENCE_SWITCHED, this.onSequenceSwitched, this);
    EventBus.on(EditorEvents.HELP_TOGGLE, this.toggleHelp, this);

    // ── History: push undo before mutations ──
    // STEP_ADDED/STEP_REMOVED/STEPS_REORDERED → always push
    EventBus.on(EditorEvents.STEP_ADDED, this.historyPushUndo, this);
    EventBus.on(EditorEvents.STEP_REMOVED, this.historyPushUndo, this);
    EventBus.on(EditorEvents.STEPS_REORDERED, this.historyPushUndo, this);
    // STEP_UPDATED → debounced (fires on every slider drag)
    EventBus.on(EditorEvents.STEP_UPDATED, this.historyPushUndoDebounced, this);
    // Reset debounce on step selection change
    EventBus.on(EditorEvents.STEP_SELECTED, this.historyResetDebounce, this);

    // ── Keyboard shortcuts ──
    this.setupKeyboardShortcuts();
  }

  // ── Keyboard shortcuts ──

  private setupKeyboardShortcuts(): void {
    // Use window keydown for Ctrl combos (Phaser's keyboard doesn't handle Ctrl+key well)
    this.keyHandler = (e: KeyboardEvent) => {
      // Don't capture when a text input dialog is open
      if (document.querySelector('.tech-input-dialog')) return;

      const ctrl = e.ctrlKey || e.metaKey;
      const shift = e.shiftKey;

      // ── Help overlay intercepts everything when open ──
      if (this.helpOverlay) {
        this.toggleHelp();
        e.preventDefault();
        return;
      }

      // ── Mode-independent shortcuts ──
      if (e.key === '?' || e.key === 'F1') {
        this.toggleHelp();
        e.preventDefault();
        return;
      }

      // ── Edit mode shortcuts ──
      if (this.state.mode === 'edit') {
        // Undo
        if (ctrl && !shift && e.key === 'z') {
          if (this.history.undo()) this.showToast('Undo');
          e.preventDefault(); return;
        }
        // Redo
        if (ctrl && shift && (e.key === 'z' || e.key === 'Z')) {
          if (this.history.redo()) this.showToast('Redo');
          e.preventDefault(); return;
        }
        // Copy
        if (ctrl && e.key === 'c') {
          this.copyStep();
          e.preventDefault(); return;
        }
        // Paste
        if (ctrl && e.key === 'v') {
          this.pasteStep();
          e.preventDefault(); return;
        }
        // Duplicate
        if (ctrl && e.key === 'd') {
          this.duplicateStep();
          e.preventDefault(); return;
        }
        // Delete
        if (e.key === 'Delete' || e.key === 'Backspace') {
          this.deleteSelectedStep();
          e.preventDefault(); return;
        }
        // Navigate left
        if (!ctrl && e.key === 'ArrowLeft') {
          this.navigateStep(-1);
          e.preventDefault(); return;
        }
        // Navigate right
        if (!ctrl && e.key === 'ArrowRight') {
          this.navigateStep(1);
          e.preventDefault(); return;
        }
        // Space = Play
        if (e.key === ' ') {
          EventBus.emit(EditorEvents.PREVIEW_START);
          e.preventDefault(); return;
        }
      }

      // ── Preview mode shortcuts ──
      if (this.state.mode === 'preview') {
        // Space = Stop
        if (e.key === ' ') {
          EventBus.emit(EditorEvents.PREVIEW_STOP);
          e.preventDefault(); return;
        }
      }

      // ── ESC (all modes) ──
      if (e.key === 'Escape') {
        if (this.state.mode === 'captureCamera' || this.state.mode === 'captureClick') {
          this.captureMode.cancel();
        } else if (this.state.mode === 'preview') {
          this.preview.stop();
        } else {
          this.confirmClose();
        }
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', this.keyHandler);
  }

  private keyHandler: ((e: KeyboardEvent) => void) | null = null;

  // ── Copy / Paste / Duplicate ──

  private copyStep(): void {
    const idx = this.state.selectedStepIndex;
    if (idx < 0 || idx >= this.state.sequence.steps.length) return;
    this.clipboardStep = JSON.parse(JSON.stringify(this.state.sequence.steps[idx]));
    this.showToast('Step copied');
  }

  private pasteStep(): void {
    if (!this.clipboardStep) { this.showToast('Nothing to paste'); return; }
    const clone = JSON.parse(JSON.stringify(this.clipboardStep));
    // Generate a new unique ID for the pasted step
    clone.id = `${clone.type}_${Date.now().toString(36)}`;
    const idx = this.state.selectedStepIndex + 1;
    const insertAt = idx >= 0 ? idx : this.state.sequence.steps.length;
    this.state.sequence.steps.splice(insertAt, 0, clone);
    this.state.selectedStepIndex = insertAt;
    EventBus.emit(EditorEvents.STEP_ADDED);
    this.showToast('Step pasted');
  }

  private duplicateStep(): void {
    this.copyStep();
    if (this.clipboardStep) this.pasteStep();
  }

  private deleteSelectedStep(): void {
    const idx = this.state.selectedStepIndex;
    if (idx < 0 || idx >= this.state.sequence.steps.length) return;
    const stepId = this.state.sequence.steps[idx].id ?? `step ${idx + 1}`;
    showConfirmDialog(this, `Delete step "${stepId}"?`, () => {
      this.state.sequence.steps.splice(idx, 1);
      if (this.state.selectedStepIndex >= this.state.sequence.steps.length) {
        this.state.selectedStepIndex = this.state.sequence.steps.length - 1;
      }
      EventBus.emit(EditorEvents.STEP_REMOVED);
    });
  }

  private navigateStep(dir: number): void {
    const len = this.state.sequence.steps.length;
    if (len === 0) return;
    let next = this.state.selectedStepIndex + dir;
    if (next < 0) next = 0;
    if (next >= len) next = len - 1;
    if (next !== this.state.selectedStepIndex) {
      this.state.selectedStepIndex = next;
      EventBus.emit(EditorEvents.STEP_SELECTED);
    }
  }

  // ── Help Overlay ──

  private toggleHelp = (): void => {
    if (this.helpOverlay) {
      this.helpOverlay.destroy();
      this.helpOverlay = null;
      return;
    }
    this.helpOverlay = this.add.container(0, 0).setDepth(200);

    // Semi-transparent backdrop
    const backdrop = this.add.rectangle(CANVAS_W / 2, CANVAS_H / 2, CANVAS_W, CANVAS_H, 0x000000, 0.75)
      .setInteractive(); // blocks clicks through
    this.helpOverlay.add(backdrop);

    // Close on click
    backdrop.on('pointerdown', () => this.toggleHelp());

    // Panel background
    const panelW = 620;
    const panelH = 380;
    const px = CANVAS_W / 2 - panelW / 2;
    const py = CANVAS_H / 2 - panelH / 2;

    const panelBg = this.add.graphics();
    fillTechRect(panelBg, px, py, panelW, panelH, 0x111133, 0.98);
    drawTechRect(panelBg, px, py, panelW, panelH, TECH.ACCENT, 0.8, 2);
    this.helpOverlay.add(panelBg);

    // Title
    const title = this.add.text(CANVAS_W / 2, py + 20, 'CINEMATIC EDITOR — QUICK REFERENCE', {
      fontSize: '14px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5, 0);
    this.helpOverlay.add(title);

    // Left column: Shortcuts
    const shortcutsText = [
      '  SHORTCUTS',
      '  ─────────',
      '  Space      Play / Stop',
      '  Ctrl+Z     Undo',
      '  Ctrl+Shift+Z  Redo',
      '  Ctrl+C     Copy step',
      '  Ctrl+V     Paste step',
      '  Ctrl+D     Duplicate step',
      '  Delete     Remove step',
      '  < >        Navigate steps',
      '  ? / F1     This help',
      '  Esc        Cancel / Close',
    ].join('\n');

    const leftCol = this.add.text(px + 20, py + 50, shortcutsText, {
      fontSize: '11px', color: '#aaccff', fontFamily: 'monospace', lineSpacing: 4,
    });
    this.helpOverlay.add(leftCol);

    // Right column: Workflow + Step types
    const workflowText = [
      '  WORKFLOW',
      '  ────────',
      '  1. Add steps with +ADD',
      '  2. Select in timeline',
      '  3. Edit in right panel',
      '  4. Preview with PLAY',
      '  5. Step-by-step with >>STEP',
      '  6. Export JSON or SAVE',
      '',
      '  STEP TYPES',
      '  ──────────',
      '  Dialogue    Camera Pan',
      '  Fake Click  Action',
      '  Wait        Recap',
    ].join('\n');

    const rightCol = this.add.text(px + panelW / 2 + 10, py + 50, workflowText, {
      fontSize: '11px', color: '#aaccff', fontFamily: 'monospace', lineSpacing: 4,
    });
    this.helpOverlay.add(rightCol);

    // Footer
    const footer = this.add.text(CANVAS_W / 2, py + panelH - 20, 'Press any key or click to close', {
      fontSize: '10px', color: '#666688', fontFamily: 'monospace',
    }).setOrigin(0.5, 1);
    this.helpOverlay.add(footer);
  };

  // ── Toast notification ──

  private showToast(msg: string): void {
    const toast = this.add.text(CANVAS_W / 2, 60, msg, {
      fontSize: '11px', color: '#44ff88', fontFamily: 'monospace', backgroundColor: '#111122',
      padding: { x: 8, y: 4 },
    }).setOrigin(0.5, 0).setDepth(150);
    this.tweens.add({
      targets: toast, alpha: 0, y: 50,
      duration: 1200, delay: 600, ease: 'Power2',
      onComplete: () => toast.destroy(),
    });
  }

  // ── Event handlers ──

  private onStepSelected(): void {
    this.stepPanel.refresh();
    this.timeline.refresh();
  }

  private onStepsChanged(): void {
    this.timeline.rebuild();
    this.stepPanel.refresh();
    this.scheduleAutoSave();
  }

  private stepUpdateTimer: Phaser.Time.TimerEvent | null = null;

  private onStepUpdated(): void {
    // Debounce both timeline and panel refresh (fires frequently during interactive camera drag/keys)
    if (this.stepUpdateTimer) this.stepUpdateTimer.remove();
    this.stepUpdateTimer = this.time.delayedCall(80, () => {
      this.timeline.refresh();
      this.stepPanel.refresh();
      this.stepUpdateTimer = null;
    });
    this.scheduleAutoSave();
  }

  /** Debounced auto-save: persists sequence to registry + localStorage after mutations */
  private scheduleAutoSave(): void {
    if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer);
    this.autoSaveTimer = setTimeout(() => {
      this.autoSaveTimer = null;
      registerSequence(JSON.parse(JSON.stringify(this.state.sequence)));
      this.originalJson = JSON.stringify(this.state.sequence);
    }, 500);
  }

  private onSequenceSwitched(): void {
    this.originalJson = JSON.stringify(this.state.sequence);
    this.timeline.rebuild();
    this.stepPanel.refresh();
    this.livePreview.renderPreview();
  }

  // ── Panel visibility (for capture/preview modes) ──

  showPanels(): void {
    this.bgGfx.setVisible(true);
    this.toolbar.setVisible(true);
    this.timeline.setVisible(true);
    this.stepPanel.setVisible(true);
    this.livePreview.setVisible(true);
    if (this.editorBorderGfx) this.editorBorderGfx.setVisible(true);
    this.livePreview.renderPreview();
  }

  hidePanels(): void {
    this.bgGfx.setVisible(false);
    this.toolbar.setVisible(false);
    this.timeline.setVisible(false);
    this.stepPanel.setVisible(false);
    this.livePreview.setVisible(false);
  }

  /** Show toolbar + timeline for preview mode, hide step panel + live preview */
  showPreviewPanels(): void {
    this.bgGfx.setVisible(false);
    this.toolbar.setVisible(true);
    this.timeline.setVisible(true);
    this.stepPanel.setVisible(false);
    // Clean up live preview objects before hiding (prevents stale handlers/graphics
    // from lingering during playback and leaking into the play view)
    this.livePreview.clearPreview();
    this.livePreview.setVisible(false);
    if (this.editorBorderGfx) this.editorBorderGfx.setVisible(false);
    this.timeline.refresh();
  }

  // ── Close ──

  /** Called by EditorToolbar after save to reset dirty flag */
  markClean(): void {
    this.originalJson = JSON.stringify(this.state.sequence);
  }

  private confirmClose(): void {
    const isDirty = JSON.stringify(this.state.sequence) !== this.originalJson;
    if (isDirty) {
      showConfirmDialog(this, 'You have unsaved changes. Close anyway?', () => this.closeEditor());
    } else {
      this.closeEditor();
    }
  }

  closeEditor(): void {
    // Force-stop any running/fast-forwarding cinematic immediately
    // (covers both active preview AND stale fast-forward from a prior stop())
    EventBus.emit(CinematicEvents.FORCE_STOP);

    // Also clean up EditorPreview state in case it's still flagged active
    if (this.state.mode === 'preview') {
      this.preview.stop();
    }

    this.lockGameInput(false);

    // Unconditionally clear camera/input locks that CinematicDirector may have set
    // (safety net — if preview was interrupted or director didn't complete cleanly)
    this.registry.set('tutorialCameraLock', false);
    this.registry.set('cinematicInputLock', false);
    this.registry.set('cinematicActive', false);

    for (const sceneName of this.state.callerScenes) {
      const s = this.scene.get(sceneName);
      if (s) this.scene.resume(sceneName);
    }
    this.scene.stop('CinematicEditorScene');
  }

  /**
   * Lock/unlock all input on GameScene and UIScene.
   * When locked, only the editor scene processes input.
   * Registry flag 'cinematicEditorActive' lets individual handlers
   * check if they should yield (e.g. for tutorial step capture).
   */
  lockGameInput(lock: boolean): void {
    for (const key of this.state.callerScenes) {
      const s = this.scene.get(key);
      if (s) {
        s.input.enabled = !lock;
        if (s.input.keyboard) s.input.keyboard.enabled = !lock;
      }
    }
    // Registry flag for code that needs to check (e.g. global key handlers)
    this.registry.set('cinematicEditorActive', lock);
  }

  // ── Drawing ──

  private editorBorderGfx: Phaser.GameObjects.Graphics | null = null;
  private editorBorderTween: Phaser.Tweens.Tween | null = null;

  private drawBackground(): void {
    this.bgGfx.clear();
    // Work area: light overlay so game map is visible behind
    this.bgGfx.fillStyle(0x0a0a18, 0.35);
    this.bgGfx.fillRect(WORK_AREA.x, WORK_AREA.y, WORK_AREA.w, WORK_AREA.h);

    // Purple pulsing border around work area
    if (!this.editorBorderGfx) {
      const gfx = this.add.graphics().setDepth(10);
      const color = 0x9944ff;
      const { x, y, w, h } = WORK_AREA;
      const layers = 6;
      for (let i = 0; i < layers; i++) {
        const alpha = 0.5 - i * 0.07;
        const lw = 3 - i * 0.4;
        gfx.lineStyle(lw, color, alpha);
        gfx.strokeRect(x + i * 2, y + i * 2, w - i * 4, h - i * 4);
      }
      this.editorBorderGfx = gfx;
      this.editorBorderTween = this.tweens.add({
        targets: gfx,
        alpha: { from: 1, to: 0.25 },
        duration: 1200,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  // ── Cleanup ──

  shutdown(): void {
    // Flush pending auto-save before teardown
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = null;
      registerSequence(JSON.parse(JSON.stringify(this.state.sequence)));
    }
    this.lockGameInput(false);
    // Safety net: ensure camera/input locks are cleared
    this.registry.set('tutorialCameraLock', false);
    this.registry.set('cinematicInputLock', false);
    this.registry.set('cinematicActive', false);
    if (this.keyHandler) { window.removeEventListener('keydown', this.keyHandler); this.keyHandler = null; }
    if (this.helpOverlay) { this.helpOverlay.destroy(); this.helpOverlay = null; }
    if (this.editorBorderTween) { this.editorBorderTween.destroy(); this.editorBorderTween = null; }
    if (this.editorBorderGfx) { this.editorBorderGfx.destroy(); this.editorBorderGfx = null; }
    // Unregister routing handlers
    EventBus.off(EditorEvents.STEP_SELECTED, this.onStepSelected, this);
    EventBus.off(EditorEvents.STEP_ADDED, this.onStepsChanged, this);
    EventBus.off(EditorEvents.STEP_REMOVED, this.onStepsChanged, this);
    EventBus.off(EditorEvents.STEPS_REORDERED, this.onStepsChanged, this);
    EventBus.off(EditorEvents.STEP_UPDATED, this.onStepUpdated, this);
    EventBus.off(EditorEvents.SEQUENCE_SWITCHED, this.onSequenceSwitched, this);
    EventBus.off(EditorEvents.HELP_TOGGLE, this.toggleHelp, this);
    // Unregister history handlers (were previously leaking — registered in create())
    EventBus.off(EditorEvents.STEP_ADDED, this.historyPushUndo, this);
    EventBus.off(EditorEvents.STEP_REMOVED, this.historyPushUndo, this);
    EventBus.off(EditorEvents.STEPS_REORDERED, this.historyPushUndo, this);
    EventBus.off(EditorEvents.STEP_UPDATED, this.historyPushUndoDebounced, this);
    EventBus.off(EditorEvents.STEP_SELECTED, this.historyResetDebounce, this);
    this.toolbar?.destroy();
    this.timeline?.destroy();
    this.stepPanel?.destroy();
    this.captureMode?.destroy();
    this.preview?.destroy();
    this.livePreview?.destroy();
  }
}
